import yup from "./yup";
import {
  JUMIO_LEVELS,
  CURRENCIES,
  USD_CONVERSIONS,
  taxNames,
  taxTypes,
  feeTypes,
  UNITED_STATES_NAMES,
  PLATFORM_CHOICES,
} from "../constants";

export const geocodingCoordinate = yup
  .object()
  .shape({ lat: yup.number().required(), lng: yup.number().required() });

export const geocodingResponse = yup.object().shape({
  components: yup
    .object()
    .shape({
      "ISO_3166-1_alpha-2": yup.string().required(), // country
      city: yup.string(),
      town: yup.string(),
      village: yup.string(),
      municipality: yup.string(),
      county: yup.string(),
      state_code: yup
        .string()
        .when("country", { is: (val) => val === "US", then: yup.string().required() }),
      continent: yup.string().required(),
      _category: yup.string().oneOf(["place"]).required(),
    })
    .atLeastOneOf(["county", "city", "town", "village"]),

  bounds: yup.object().shape({
    northeast: geocodingCoordinate,
    southwest: geocodingCoordinate,
  }),
  annotations: yup.object().shape({
    currency: yup.object().shape({ iso_code: yup.string().required() }),
    roadinfo: yup.object().shape({ speed_in: yup.string().required() }),
    timezone: yup.object().shape({ name: yup.string().required() }),
  }),
  geometry: geocodingCoordinate,
});

export const newFleetForm = yup.object().shape({
  country: yup.string().required(),
  state: yup.string().when("country", {
    is: (val) => UNITED_STATES_NAMES.includes(val.trim().toLowerCase()),
    then: yup.string().length(2).trim().uppercase().required(),
    otherwise: yup.string().length(0).trim(),
  }),
  city: yup.string().min(3).required(),
  abbreviation: yup.string().length(3).trim().uppercase().required(),
});

export const newFleetFormOnSubmit = yup.object().shape({
  country: yup.string().trim(),
  city: yup.string().trim(),
});

export const generalFleetSettingsSchema = yup.object().shape(
  {
    critical_battery_threshold: yup.number().required().integer().min(1).max(99),
    first_ride_speed: yup
      .number()
      .transform((value, original) => (Number.isNaN(value) && original === "" ? null : value))
      .positive()
      .integer()
      .nullable(),
    slow_first_ride: yup.boolean().when("first_ride_speed", {
      // this field is set via cast and not user
      is: (val) => val !== null,
      then: yup.boolean().transform(() => true),
      otherwise: yup.boolean().transform(() => false),
    }),
    slow_rides_count: yup.number().when("first_ride_speed", {
      // this field is set via cast and not user
      is: (val) => val !== null,
      then: yup.number().transform(() => 1),
      otherwise: yup.number().transform(() => 0),
    }),
    operating_hours_begin: yup.string().when("operating_hours_soft_end", {
      is: (val) => val !== null,
      then: yup
        .string()
        .trim()
        .transform((value) => (value === "" ? null : value))
        .matches(/^(?:2[0-3]|[01][0-9]):[0-5][0-9](:[0-5][0-9])?$/)
        .nullable()
        .required(),
      otherwise: yup
        .string()
        .nullable()
        .transform(() => null),
    }),
    operating_hours_soft_end: yup.string().when("operating_hours_begin", {
      is: (val) => val !== null,
      then: yup
        .string()
        .trim()
        .transform((value) => (value === "" ? null : value))
        .matches(/^(?:2[0-3]|[01][0-9]):[0-5][0-9](:[0-5][0-9])?$/)
        .nullable()
        .required(),
      otherwise: yup
        .string()
        .nullable()
        .transform(() => null),
    }),
    warn_soft_end: yup.number().when(["operating_hours_begin", "operating_hours_soft_end"], {
      is: (begin, end) => begin !== null && end !== null,
      then: yup
        .number()
        .transform((value, original) => (Number.isNaN(value) && original === "" ? null : value))
        .positive()
        .integer()
        .nullable(),
      otherwise: yup
        .number()
        .transform(() => null)
        .nullable(),
    }),
    require_parking_photo: yup.boolean().required(),
    enable_sidewalk_notifications: yup.boolean().required(),
    lock_to_enabled: yup.boolean().required(),
    minimum_age: yup
      .number()
      .transform((value, original) => (Number.isNaN(value) && original === "" ? null : value))
      .positive()
      .integer()
      .nullable(),
    required_jumio_level: yup
      .string()
      .trim()
      .transform((value) => (!JUMIO_LEVELS.includes(value) ? JUMIO_LEVELS[0] : value))
      .oneOf(JUMIO_LEVELS),
    deployment_reservation_limit: yup
      .number()
      .transform((value, original) => (Number.isNaN(value) && original === "" ? null : value))
      .positive()
      .integer()
      .nullable(),
    required_survey_template_uuid: yup
      .string()
      .trim()
      .when("survey_enabled", {
        is: (val) => val === true,
        then: yup.string().required(),
        otherwise: yup
          .string()
          .nullable()
          .transform((value) => (value === "" ? null : value)),
      }),
    required_survey_ride_count: yup
      .number()
      .required()
      .when("survey_enabled", {
        is: (val) => val === true,
        then: yup.number().min(0).max(10000).integer(),
        otherwise: yup
          .number()
          .transform(() => 0)
          .nullable(),
      }),
    required_survey_refresh_days: yup
      .number()
      .required()
      .when("survey_enabled", {
        is: (val) => val === true,
        then: yup.number().min(0).max(10000).integer(),
        otherwise: yup
          .number()
          .transform(() => 0)
          .nullable(),
      }),
    survey_enabled: yup.boolean().required(),
    first_survey_offset: yup.number().min(0).max(10000).integer(),
    notification_email: yup.string().trim().email("Invalid email format").optional(),
    default_firmware_package: yup.string().nullable(),
    default_geofence_package: yup.string().nullable(),
    is_bookable: yup.boolean().required(),
    platform: yup
      .string()
      .trim()
      .transform((value) => (!PLATFORM_CHOICES.includes(value) ? PLATFORM_CHOICES[0] : value))
      .oneOf(PLATFORM_CHOICES)
      .required(),
  },
  ["operating_hours_begin", "operating_hours_soft_end"], // arg avoids cyclic dependency issue
);

export const billingFleetSettingsSchema = yup.object().shape({
  currency: yup
    .string()
    .trim()
    .length(3)
    .transform((value) => (!CURRENCIES.includes(value) ? CURRENCIES[0] : value))
    .oneOf(CURRENCIES)
    .required(),
  flat_fee: yup.number().positive().integer().min(0),
  time_unit_fee: yup.number().positive().integer().min(0),
  min_fleet_charge_amount: yup.number().positive().integer().min(0).nullable(),
  min_fleet_charge_minutes: yup.number().positive().integer().min(0).nullable(),
  first_card_bonus: yup.number().positive().integer().min(0),
  rate_plan_fine_types: yup.array().of(
    yup.object().shape({
      id: yup.string().notRequired(),
      fine_type_id: yup.string().required(),
      fine_type_is_revenue: yup.boolean().required(),
      fine_type_name: yup.string().required(),
      name: yup.string().required(),
      amount: yup.number().positive().min(0).required(),
    }),
  ),
  auto_renew_options: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          id: yup.string().notRequired(),
          rate_plan_id: yup.string().notRequired(),
          amount: yup.number().positive().min(0).required(),
          bonus: yup.number().min(0).required(),
        })
        .omit(["id", "rate_plan_id"]),
    )
    .nullable(),
  tax: yup
    .object()
    .shape({
      id: yup.string().notRequired(),
      name: yup.string().trim().oneOf(taxNames),
      type: yup.string().trim().oneOf(taxTypes),
      rate: yup.number().min(0).max(99),
    })
    .omit(["id"])
    .nullable(),
  fees: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          id: yup.string().notRequired(),
          rate_plan_id: yup.string().notRequired(),
          name: yup.string().trim().required(),
          type: yup.string().trim().oneOf(feeTypes).required(),
          amount: yup.number().required().positive(),
          active: yup.boolean().notRequired(),
        })
        .omit(["id", "rate_plan_id"]),
    )
    .nullable(),
  blocking_amount: yup.number().positive().integer().min(0).nullable(),
  min_limit_on_card_no_hold: yup.number().positive().integer().min(0).nullable(),
  min_limit_no_card: yup.number().positive().integer().min(0).nullable(),
  is_default: yup
    .boolean()
    .nullable()
    .transform(() => null),
  group_rides_hold_adjustment: yup
    .number()
    .nullable(true)
    .transform((value, original) => (Number.isNaN(value) && original === "" ? null : value))
    .notRequired(),
});

export const messagingFleetSettingsSchema = yup.object().shape({
  message: yup.string().max(200).trim().required(),
  hyperlink: yup
    .string()
    .trim()
    .matches(/^$|https?:\/\/(www.)?[A-Za-z0-9+&@#/%?=~_|!:,.;]+.[A-Za-z0-9+&@#/%=~_|]+/)
    .optional(),
  dismissable: yup.boolean().required(),
  begin: yup.date().required(),
  end: yup.date().min(yup.ref("begin")).typeError("Invalid Date").required(),
});

const maxUSDPassOfferPrice = 100000;
export const passesFleetSettingsSchema = yup.object({
  pass_offer_type_id: yup.string().required(),
  description: yup.string().max(250).trim().optional(),
  currency: yup.string().oneOf(CURRENCIES).required(),
  start_date: yup.date().required(),
  end_date: yup.date().min(yup.ref("start_date")).typeError("Invalid Date").required(),
  price: yup
    .number()
    .integer()
    .min(0)
    .test({
      name: "maxUSDPassOfferPrice",
      // eslint-disable-next-line no-template-curly-in-string
      message: "${path} must be <= ${maxUSDPassOfferPrice} undecimated USD",
      exclusive: true,
      params: { maxUSDPassOfferPrice },
      // eslint-disable-next-line object-shorthand, func-names
      test: function (value) {
        // eslint-disable-next-line babel/no-invalid-this
        return value <= Number(maxUSDPassOfferPrice) * USD_CONVERSIONS[this.parent.currency];
      },
    })
    .required(),
  fleet: yup.string(),
  disabled: yup.boolean().default(false).optional(),
  visible: yup.boolean().default(false).optional(),
});

const maxUSDBonus = 10000;
const basePromoSchema = yup.object({
  code: yup
    .string()
    .min(6)
    .uppercase()
    .trim()
    .matches(/^[A-Z0-9-]*$/)
    .required(),
  active: yup.boolean().required(),
  max_redemptions: yup.number().integer().positive().max(100000).required(),
  expired: yup.date().required(),
  description: yup.string().max(250).trim().optional(),
  percent_off: yup.number().integer().min(0).max(100).required(),
  currency: yup.string().oneOf(CURRENCIES).required(),
  amount: yup
    .number()
    .integer()
    .positive()
    .test({
      name: "maxUSDBonus",
      // eslint-disable-next-line no-template-curly-in-string
      message: "${path} must be <= ${maxUSDBonus} undecimated USD",
      exclusive: true,
      params: { maxUSDBonus },
      // eslint-disable-next-line object-shorthand, func-names
      test: function (value) {
        // eslint-disable-next-line babel/no-invalid-this
        return value <= Number(maxUSDBonus) * USD_CONVERSIONS[this.parent.currency];
      },
    })
    .required(),
  fleets: yup.array().nullable().optional(),
});
export const promoSchemas = {
  unlock: basePromoSchema.omit(["percent_off", "amount", "currency"]),
  discount: basePromoSchema.omit(["amount", "currency"]),
  bonus: basePromoSchema.omit(["percent_off"]),
};

export const promoGroupSchema = yup.object({
  name: yup
    .string()
    .max(250)
    .trim()
    .matches(/^[a-zA-Z0-9-\s]*$/)
    .required(),
  promo_code_type: yup.string().max(30).required(),
  prefix: yup
    .string()
    .max(250)
    .trim()
    .matches(/^[a-zA-Z0-9-]*$/)
    .required(),
  description: yup.string().max(250).trim().optional(),
  currency: yup.string().max(10).required(),
  expired: yup.date().required(),
  num_codes: yup.number().min(1).integer().required(),
  max_redemptions: yup.number().min(1).integer().required(),
  active: yup.bool().optional(),
  amount: yup.number().min(0).required(),
  percent_off: yup.number().min(0).max(100).required(),
  fleets: yup.array().nullable().optional(),
});
